import { useMemo, useState } from 'react';
import { FormInstance, Modal, notification } from 'antd';
import ApiService from 'src/services/api-service';
import { ProductInfo } from 'src/types/common.types';
import {
  formatOutboundRequestIdsForApi,
  getOutboundItemSku,
  normalizeOutboundRequestId,
  parseOutboundOrderDetailsResponse,
  parseOutboundRequestIdsStored,
  parseOutboundTagsFromOrder,
  mergeImportedProducts,
} from './outbound-import.utils';
import { OutboundModalItem, OutboundOrderDetailItem, OutboundRequestTag, ProductItem } from './entry.types';

type UseOutboundImportOptions = {
  form: FormInstance;
  products: ProductInfo[] | undefined;
  formProducts: ProductItem[];
  onFormValuesChange: (changedValues: any, allValues: any) => void;
};

export function useOutboundImport({ form, products, formProducts, onFormValuesChange }: UseOutboundImportOptions) {
  const [outboundInputId, setOutboundInputId] = useState('');
  const [importedOutboundTags, setImportedOutboundTags] = useState<OutboundRequestTag[]>([]);
  const [outboundModalOpen, setOutboundModalOpen] = useState(false);
  const [outboundModalItems, setOutboundModalItems] = useState<OutboundModalItem[]>([]);
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [pendingOutboundId, setPendingOutboundId] = useState('');
  const [pendingOutboundStatus, setPendingOutboundStatus] = useState<'valid' | 'cancelled'>('valid');

  const existingSkuQuantities = useMemo(() => {
    const map = new Map<string, number>();
    formProducts.forEach((product: ProductItem) => {
      const sku = (product.code || '').toLowerCase();
      if (!sku) {
        return;
      }
      map.set(sku, (map.get(sku) || 0) + Number(product.quantity || 0));
    });
    return map;
  }, [formProducts]);

  const isDuplicateOutboundSku = (record: OutboundModalItem) => {
    const sku = getOutboundItemSku(record).toLowerCase();
    return !!sku && existingSkuQuantities.has(sku);
  };

  const syncOutboundRequestIds = (tags: OutboundRequestTag[]) => {
    form.setFieldsValue({
      outboundRequestId: formatOutboundRequestIdsForApi(tags),
    });
  };

  const getImportedOutboundRequestIds = () => {
    const fromTags = importedOutboundTags.map(tag => tag.id);
    const fromForm = parseOutboundRequestIdsStored(form.getFieldValue('outboundRequestId'));
    const ids = [...fromTags, ...fromForm];
    return ids.filter((id, index) => ids.indexOf(id) === index);
  };

  const confirmDuplicateOutboundRequestId = (outboundId: string): Promise<boolean> => {
    if (!getImportedOutboundRequestIds().includes(outboundId)) {
      return Promise.resolve(true);
    }
    return new Promise(resolve => {
      Modal.confirm({
        title: 'Cảnh báo trùng mã xuất kho',
        content: (
          <div>
            <div className="mb-2">Mã xuất kho sau đã được import vào đơn hàng:</div>
            <div>• #{outboundId}</div>
            <div className="mt-2">Bạn có muốn tiếp tục import không?</div>
          </div>
        ),
        okText: 'Tiếp tục',
        cancelText: 'Hủy',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const mapOutboundItemToProduct = (item: OutboundOrderDetailItem) => {
    const sku = getOutboundItemSku(item);
    const catalogProduct =
      products?.find((product: ProductInfo) => product.id === item.productId) ||
      products?.find((product: ProductInfo) => product.code?.toLowerCase() === sku.toLowerCase());
    const outboundNote = [
      item.color ? `Màu: ${item.color}` : null,
      item.note || null,
      item.uid ? `UID: ${item.uid}` : null,
    ]
      .filter(Boolean)
      .join(' | ');

    return {
      productId: catalogProduct?.id || item.productId,
      code: sku || catalogProduct?.code,
      name: item.name || catalogProduct?.name,
      quantity: item.quantity || 1,
      price: 0,
      vat: item.vat ?? catalogProduct?.vat ?? 0,
      vatAmount: 0,
      minPrice: item.minPrice ?? catalogProduct?.minPrice,
      maxPrice: item.maxPrice ?? catalogProduct?.maxPrice,
      outboundNote: outboundNote || undefined,
      workItems: [],
    };
  };

  const fetchOutboundOrderDetails = async (outboundId: string) => {
    const response = await ApiService.get(`/api/order-requests/${outboundId}`);
    if (response.status !== 200) {
      const message =
        response.data?.message ||
        `Mã xuất kho #${outboundId} không tồn tại hoặc đã bị hủy bởi bộ phận Kho. Vui lòng kiểm tra lại.`;
      throw new Error(message);
    }
    return parseOutboundOrderDetailsResponse(response.data);
  };

  const handleImportOutbound = async () => {
    const outboundId = normalizeOutboundRequestId(outboundInputId);
    if (!outboundId) {
      notification.warning({ message: 'Vui lòng nhập mã xuất kho' });
      return;
    }
    const shouldContinue = await confirmDuplicateOutboundRequestId(outboundId);
    if (!shouldContinue) {
      return;
    }

    try {
      setOutboundLoading(true);
      const { items, status } = await fetchOutboundOrderDetails(outboundId);
      if (!items.length) {
        notification.warning({
          message: `Lệnh xuất kho #${outboundId} không có sản phẩm để import`,
        });
        return;
      }
      setPendingOutboundId(outboundId);
      setPendingOutboundStatus(status);
      setOutboundModalItems(
        items.map((item, index) => ({
          ...item,
          key: `${outboundId}-${index}`,
          selected: true,
        })),
      );
      setOutboundModalOpen(true);
    } catch (error: any) {
      notification.error({
        message: 'Lỗi khi lấy lệnh xuất kho',
        description: error.message || 'Không thể kết nối với hệ thống Kho. Vui lòng thử lại sau.',
      });
    } finally {
      setOutboundLoading(false);
    }
  };

  const handleConfirmOutboundImport = async () => {
    const selectedItems = outboundModalItems.filter(item => item.selected);
    if (!selectedItems.length) {
      notification.warning({ message: 'Vui lòng chọn ít nhất một sản phẩm để import' });
      return;
    }

    const currentProducts = form.getFieldValue('products') || [];
    const existingSkus = new Set(
      currentProducts.map((product: ProductItem) => (product.code || '').toLowerCase()).filter(Boolean),
    );
    const duplicateSkus = selectedItems
      .map(item => getOutboundItemSku(item).toLowerCase())
      .filter((sku, index, arr) => sku && existingSkus.has(sku) && arr.indexOf(sku) === index);

    if (duplicateSkus.length) {
      const shouldContinueSku = await new Promise<boolean>(resolve => {
        Modal.confirm({
          title: 'Cảnh báo trùng SKU',
          content: (
            <div>
              <div className="mb-2">Các sản phẩm sau đã tồn tại trong đơn:</div>
              {duplicateSkus.map(sku => (
                <div key={sku}>• {sku.toUpperCase()}</div>
              ))}
              <div className="mt-2">Bạn có muốn tiếp tục import không?</div>
            </div>
          ),
          okText: 'Tiếp tục',
          cancelText: 'Hủy',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!shouldContinueSku) {
        return;
      }
    }

    const storedOutboundId = normalizeOutboundRequestId(outboundInputId);
    const newProducts = selectedItems.map(mapOutboundItemToProduct);
    const hasExistingTag = importedOutboundTags.some(tag => tag.id === storedOutboundId);
    const updatedTags = hasExistingTag
      ? importedOutboundTags
      : [...importedOutboundTags, { id: storedOutboundId, status: pendingOutboundStatus }];
    const allValues = {
      ...form.getFieldsValue(),
      products: mergeImportedProducts(currentProducts, newProducts),
    };

    form.setFieldsValue(allValues);
    setImportedOutboundTags(updatedTags);
    syncOutboundRequestIds(updatedTags);
    setOutboundModalOpen(false);
    setOutboundInputId('');
    onFormValuesChange({ products: allValues.products }, allValues);

    notification.success({
      message: `Đã import ${selectedItems.length} sản phẩm từ lệnh xuất kho #${storedOutboundId}`,
    });
  };

  const handleRemoveOutboundTag = (outboundId: string) => {
    Modal.confirm({
      title: 'Gỡ liên kết mã lệnh',
      content: `Bạn có chắc chắn muốn gỡ liên kết mã xuất kho #${outboundId}? Sản phẩm trong danh sách sẽ không bị xóa tự động.`,
      okText: 'Xác nhận',
      cancelText: 'Quay lại',
      onOk: () => {
        const updatedTags = importedOutboundTags.filter(tag => tag.id !== outboundId);
        setImportedOutboundTags(updatedTags);
        syncOutboundRequestIds(updatedTags);
      },
    });
  };

  const validateOutboundTagsBeforeSave = (): boolean => {
    const cancelledTag = importedOutboundTags.find(tag => tag.status === 'cancelled');
    if (cancelledTag) {
      notification.error({
        message: 'Không thể lưu đơn hàng',
        description: `Lệnh xuất kho #${cancelledTag.id} đã bị hủy. Vui lòng xóa mã này khỏi đơn hàng và sử dụng mã xuất kho thay thế.`,
      });
      return false;
    }
    return true;
  };

  const loadOutboundTagsFromOrder = (data: any) => {
    const outboundTags = parseOutboundTagsFromOrder(data);
    setImportedOutboundTags(outboundTags);
    return formatOutboundRequestIdsForApi(outboundTags);
  };

  const resetOutboundImport = () => {
    setImportedOutboundTags([]);
    setOutboundInputId('');
  };

  return {
    outboundInputId,
    setOutboundInputId,
    importedOutboundTags,
    outboundModalOpen,
    setOutboundModalOpen,
    outboundModalItems,
    setOutboundModalItems,
    outboundLoading,
    pendingOutboundId,
    existingSkuQuantities,
    isDuplicateOutboundSku,
    handleImportOutbound,
    handleConfirmOutboundImport,
    handleRemoveOutboundTag,
    validateOutboundTagsBeforeSave,
    loadOutboundTagsFromOrder,
    resetOutboundImport,
  };
}
